/**
 * A MODULAÇÃO — quantos peixes aquela represa comporta.
 *
 * Existe por um motivo operacional que o gestor deu: a equipe precisa saber
 * **antes de começar** quanto vai em cada represa, sem ter de ligar para ele no
 * meio da transferência, com o trator carregado e o oxigênio da caixa caindo.
 *
 *     peixes = carga alvo (t/ha) × área (ha) × 1.000.000
 *              ──────────────────────────────────────────
 *                       peso final alvo (g)
 *
 * Nenhum dos três números é palpite: a carga alvo e a área estão no cadastro do
 * tanque, e o peso final alvo é a decisão comercial da fazenda. A conta é a
 * densidade que se pretende ter NO FIM, convertida para cabeças agora.
 *
 * ── O QUE ESTA TELA NÃO FAZ, e é decisão do gestor ────────────────────────
 *
 * Não prevê viagens. «Cada piscicultura tem diferentes ferramentas: uns usam
 * caixa d'água de 1000 litros, outros tambor de 200 litros… é COMPLICADO SER
 * ASSERTIVO nisso, prefiro que NÃO avance neste sentido de predição.»
 *
 * Ele está certo, e o que eu teria para oferecer era pior que silêncio: aprender
 * o tamanho da carga na primeira viagem da própria operação — mas a primeira
 * viagem pode ter saído pela metade, e um palpite errado sobre equipamento que
 * não conheço estraga a confiança no resto da tela.
 *
 * O que fica é a conversão para QUILO, que não é predição nenhuma: é aritmética
 * com a média que eles acabaram de medir. Quilo é a língua da carga — quem está
 * carregando sabe quantos quilos cabem na caixa dele e faz a divisão sozinho.
 */

import { m2ParaHa, type Hectares, type MetrosQuadrados } from './unidades'

export interface Alvo {
  /** Quantos peixes a represa comporta, pela carga alvo e pelo peso final. */
  peixes: number
  /** Quantos ainda faltam para fechar. Zero quando o alvo já foi atingido. */
  faltam: number
  /** Fração de 0 a 1 — a barra. Passa de 1 quando encheu além do alvo. */
  fracao: number
  /**
   * O que falta, em quilo, pela média medida NESTA operação. Nulo sem média:
   * inventar uma converteria a aritmética em palpite.
   */
  faltamKg: number | null
}

/**
 * O alvo do tanque, ou nulo quando o cadastro não tem o que é preciso.
 *
 * Nulo é resposta legítima e a tela respeita: tanque sem carga alvo cadastrada
 * simplesmente não mostra barra nenhuma. Chutar um alvo seria pior — a equipe
 * pararia de encher numa marca inventada.
 */
export function modular(
  areaM2: MetrosQuadrados,
  cargaAlvoTHa: number | null,
  pesoFinalAlvoG: number | null,
  jaContados: number,
  mediaG: number | null,
): Alvo | null {
  if (!cargaAlvoTHa || cargaAlvoTHa <= 0) return null
  if (!pesoFinalAlvoG || pesoFinalAlvoG <= 0) return null
  if (!areaM2 || areaM2 <= 0) return null

  const ha: Hectares = m2ParaHa(areaM2)
  const peixes = Math.round((cargaAlvoTHa * ha * 1_000_000) / pesoFinalAlvoG)
  if (peixes <= 0) return null

  const faltam = Math.max(0, peixes - jaContados)
  return {
    peixes,
    faltam,
    fracao: jaContados / peixes,
    faltamKg: mediaG && mediaG > 0 ? (faltam * mediaG) / 1000 : null,
  }
}

/**
 * A DIVERGÊNCIA entre o que saiu e o que chegou — mostrada, nunca consertada.
 *
 * A saída foi medida em QUILO e os peixes vêm da divisão pela média; a entrada
 * foi medida CONTANDO peixe. São dois instrumentos, e dois instrumentos sempre
 * discordam um pouco. Forçar a igualdade significaria escolher uma régua e
 * jogar a outra fora — e a outra também era uma medição.
 *
 * É a regra 4 do projeto em escala menor: divergência de população é normal e
 * aceita, é diagnóstico e nunca acusação. Os peixes que sobram vão parar no
 * saldo residual do encerramento da origem, que é onde o projeto já guarda o
 * que não fecha.
 *
 * E há um número que simplesmente não precisa existir: quantos peixes sobraram
 * em cima do trator. Era ele que não fechava. A remessa acaba quando a carreta
 * esvazia, e os números são os que foram contados.
 */
export function divergencia(
  saidaKg: number,
  mediaG: number | null,
  contadosNoDestino: number,
): { estimadosNaSaida: number; diferenca: number; fracao: number } | null {
  if (!mediaG || mediaG <= 0 || saidaKg <= 0) return null
  const estimadosNaSaida = Math.round((saidaKg * 1000) / mediaG)
  if (estimadosNaSaida <= 0) return null
  return {
    estimadosNaSaida,
    diferenca: contadosNoDestino - estimadosNaSaida,
    fracao: (contadosNoDestino - estimadosNaSaida) / estimadosNaSaida,
  }
}

export type SemLotacao = 'sem_carga' | 'sem_peso_alvo' | 'sem_area' | 'nao_cabe'

/**
 * Por que `modular` devolveu nulo — para a tela DIZER, em vez de a barra sumir.
 *
 * A barra sumia calada, e o gestor leu o sumiço como defeito da data: estava
 * testando com o dia de ontem, e foi a explicação que sobrava. A causa era
 * outra — a carga-alvo só estava cadastrada no R-2 —, e nada na tela dizia.
 * Cobre TODO nulo de `modular`, na mesma ordem, inclusive a conta que arredonda
 * para zero peixe — senão sobraria um caso em que a barra some calada.
 */
export function porQueNaoModula(
  areaM2: MetrosQuadrados,
  cargaAlvoTHa: number | null,
  pesoFinalAlvoG: number | null,
): SemLotacao | null {
  if (!cargaAlvoTHa || cargaAlvoTHa <= 0) return 'sem_carga'
  if (!pesoFinalAlvoG || pesoFinalAlvoG <= 0) return 'sem_peso_alvo'
  if (!areaM2 || areaM2 <= 0) return 'sem_area'
  if (Math.round((cargaAlvoTHa * m2ParaHa(areaM2) * 1_000_000) / pesoFinalAlvoG) <= 0) {
    return 'nao_cabe'
  }
  return null
}
